import React from "react";
import { useHistory } from "react-router-dom";
import { Button } from "../../shadcn/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { QrCode, Smartphone, Download, ArrowLeft } from "lucide-react";

const QRDemoScreen = () => {
  const history = useHistory();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" onClick={() => history.goBack()} className="mb-4" {...({} as any)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">QR Code Demo</h1>
            <p className="text-xl text-gray-600">Test our smart QR code system with device detection</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* QR Code Generator */}
          <Card className="p-6" {...({} as any)}>
            <CardHeader {...({} as any)}>
              <CardTitle className="flex items-center gap-2" {...({} as any)}>
                <QrCode className="w-6 h-6" />
                Generate QR Code
              </CardTitle>
              <CardDescription {...({} as any)}>Create a smart QR code that detects device type</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4" {...({} as any)}>
              <p className="text-sm text-gray-600">
                Our QR code system automatically detects whether a user is on iOS, Android, or Desktop and redirects
                them to the appropriate app store or download page.
              </p>

              <Button onClick={() => history.push("/qr-code")} className="w-full" size="lg" {...({} as any)}>
                <QrCode className="w-5 h-5 mr-2" />
                Generate Smart QR Code
              </Button>
            </CardContent>
          </Card>

          {/* Features */}
          <Card className="p-6" {...({} as any)}>
            <CardHeader {...({} as any)}>
              <CardTitle className="flex items-center gap-2" {...({} as any)}>
                <Smartphone className="w-6 h-6" />
                Features
              </CardTitle>
              <CardDescription {...({} as any)}>What makes our QR code system special</CardDescription>
            </CardHeader>
            <CardContent {...({} as any)}>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 text-xs font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Device Detection</h3>
                    <p className="text-xs text-gray-600">Automatically detects iOS, Android, or Desktop devices</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 text-xs font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Smart Redirects</h3>
                    <p className="text-xs text-gray-600">Takes users to the right app store for their device</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 text-xs font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">URL Shortening</h3>
                    <p className="text-xs text-gray-600">Creates clean, short URLs for better QR codes</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-blue-600 text-xs font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Analytics Tracking</h3>
                    <p className="text-xs text-gray-600">Track QR code scans and conversion rates</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How to Use */}
        <Card className="mt-8 p-6" {...({} as any)}>
          <CardHeader {...({} as any)}>
            <CardTitle {...({} as any)}>How to Use</CardTitle>
          </CardHeader>
          <CardContent {...({} as any)}>
            <div className="grid md:grid-cols-3 gap-6 text-sm">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 font-bold">1</span>
                </div>
                <h3 className="font-semibold mb-2">Generate QR Code</h3>
                <p className="text-gray-600">Click the button above to generate a smart QR code</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 font-bold">2</span>
                </div>
                <h3 className="font-semibold mb-2">Test Device Detection</h3>
                <p className="text-gray-600">Try scanning with different devices to see the smart redirects</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-green-600 font-bold">3</span>
                </div>
                <h3 className="font-semibold mb-2">Share & Track</h3>
                <p className="text-gray-600">Share the QR code and track analytics on redirects</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Details */}
        <Card className="mt-8 p-6" {...({} as any)}>
          <CardHeader {...({} as any)}>
            <CardTitle {...({} as any)}>Technical Implementation</CardTitle>
          </CardHeader>
          <CardContent {...({} as any)}>
            <div className="space-y-4 text-sm">
              <div>
                <h3 className="font-semibold mb-2">Device Detection Logic</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>iOS: Detects iPhone, iPad, iPod using user agent</li>
                  <li>Android: Detects Android devices and Windows Phone</li>
                  <li>Desktop: Fallback for non-mobile devices</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">URL Shortening</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Integrates with URL shortener services (short.io, bit.ly, etc.)</li>
                  <li>Fallback to internal redirect URLs if service unavailable</li>
                  <li>Analytics tracking for QR code scans</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">App Store URLs</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>iOS: Redirects to App Store</li>
                  <li>Android: Redirects to Google Play Store</li>
                  <li>Desktop: Redirects to web download page</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QRDemoScreen;
